import { lazy } from 'react';

import { lazyNamed } from 'src/utils/lazyNamed';
import AnalogProducts from './containers/AnalogProducts';
import { BroadcastProducts } from './containers/BroadcastProducts';
import {
  AdMedium,
  AdMediumMapping,
  AdPrice,
  AdTypes,
  Layout as DigitalProductsLayout,
  Placement,
  Products,
} from './containers/DigitalProducts';
import { PartnerFilialManagement } from './containers/PartnerFilialManagement';
import PointOfSaleProducts from './containers/PointOfSaleProducts';
import { PositionGroupsV2 } from './containers/PositionGroupsV2';
import {
  AdMedias,
  Availabilities,
  Calendars,
  Products as PrintProducts,
  AdPrices as PrintProductsAdPrices,
  AdTypes as PrintProductsAdTypes,
  Layout as PrintProductsLayout,
  Placements as PrintProductsPlacements,
  SizeConfigs,
} from './containers/PrintProducts2';
import { ReferencePerAdmedia } from './containers/ReferencePerAdmedia';

export default {
  Helmet: lazy(() => import('./components/Helmet')),
  CampaignTypes: lazy(() => import('./containers/CampaignTypes')),
  DataImport: lazy(() => import('./containers/DataImport')),
  Industries: lazy(() => import('./containers/Industries')),
  Labels: lazy(() => import('./containers/Labels')),
  PriceLists: lazy(() => import('./containers/PriceLists')),
  RoleBasedRedirector: lazy(() => import('./components/RoleBasedRedirector')),

  DigitalProducts: {
    Placement,
    Layout: DigitalProductsLayout,
    AdMedium,
    Products,
    AdPrice,
    AdTypes,
    AdMediumMapping,
  },

  BroadcastProducts,

  CompanySettings: {
    Layout: lazyNamed(() => import('./containers/CompanySettings/components/Layout'), 'Layout'),
    SalesUnitsProvider: lazyNamed(() => import('./containers/CompanySettings/components/SalesUnitsProvider'), 'SalesUnitsProvider'),
    EmailTemplates: lazyNamed(() => import('./containers/CompanySettings/containers/EmailTemplates'), 'EmailTemplates'),
    EmailTemplateForm: lazyNamed(() => import('./containers/CompanySettings/containers/EmailTemplateForm'), 'EmailTemplateForm'),
    PDFFooterTemplates: lazyNamed(() => import('./containers/CompanySettings/containers/PDFFooterTemplates'), 'PDFFooterTemplates'),
    PaymentDetails: lazyNamed(() => import('./containers/CompanySettings/containers/PaymentDetails'), 'PaymentDetails'),
    PaymentDetailsForm: lazyNamed(() => import('./containers/CompanySettings/containers/PaymentDetailsForm'), 'PaymentDetailsForm'),
    PDFStyles: lazyNamed(() => import('./containers/CompanySettings/containers/PDFStyles'), 'PDFStyles'),
    PDFTypeConfigurations: lazyNamed(
      () => import('./containers/CompanySettings/containers/PDFTypeConfigurations'),
      'PDFTypeConfigurations',
    ),
    CoopWeeks: lazyNamed(() => import('./containers/CompanySettings/containers/CoopWeeks'), 'CoopWeeks'),
    Sequencing: lazyNamed(() => import('./containers/CompanySettings/containers/Sequencing'), 'Sequencing'),
    PPTReportConfig: lazyNamed(() => import('./containers/CompanySettings/containers/PPTReportConfig'), 'PPTReportConfig'),
  },

  AdSenseSettings: {
    Credentials: lazy(() => import('./containers/AdSense/Credentials')),
    Mappings: lazy(() => import('./containers/AdSense/Mappings')),
    OauthCallback: lazy(() => import('./containers/AdSense/Credentials/OauthCallback')),
    Layout: lazy(() => import('./containers/AdSense/Layout')),
  },

  XandrProgrammatic: {
    Overview: lazy(() => import('./containers/XandrProgrammatic/containers/Overview')),
    WebsitesMapping: lazy(() => import('./containers/XandrProgrammatic/containers/WebsitesMapping')),
    BrandsMapping: lazy(() => import('./containers/XandrProgrammatic/containers/BrandsMapping')),
    Deals: lazy(() => import('./containers/XandrProgrammatic/containers/Deals')),
    DealsDBE: lazy(() => import('./containers/XandrProgrammatic/containers/DealsDBE')),
    BuyerMapping: lazyNamed(() => import('./containers/XandrProgrammatic/containers/BuyerMapping'), 'BuyerMapping'),
    Layout: lazy(() => import('./containers/XandrProgrammatic/components/Layout')),
    OverviewRedirect: lazy(() => import('./containers/XandrProgrammatic/components/OverviewRedirect')),
  },

  GoogleProgrammatic: {
    WebsitesMapping: lazy(() => import('./containers/GoogleProgrammatic/containers/WebsitesMapping')),
    BrandsMapping: lazy(() => import('./containers/GoogleProgrammatic/containers/BrandsMapping')),
    DealsDBE: lazy(() => import('./containers/GoogleProgrammatic/containers/DealsDBE')),
    BuyerMapping: lazyNamed(() => import('./containers/GoogleProgrammatic/containers/BuyerMapping'), 'BuyerMapping'),
    Layout: lazy(() => import('./containers/GoogleProgrammatic/Layout')),
  },

  AdomikProgrammatic: {
    Layout: lazyNamed(() => import('./containers/AdomikProgrammatic/components/Layout'), 'Layout'),
    WebsiteMappings: lazyNamed(() => import('./containers/AdomikProgrammatic/pages/WebsiteMappings'), 'WebsiteMappings'),
    BrandMappings: lazyNamed(() => import('./containers/AdomikProgrammatic/pages/BrandMappings'), 'BrandMappings'),
    BuyerMappings: lazyNamed(() => import('./containers/AdomikProgrammatic/pages/BuyerMappings'), 'BuyerMappings'),
    Deals: lazyNamed(() => import('./containers/AdomikProgrammatic/pages/Deals'), 'Deals'),
    SSPMappings: lazyNamed(() => import('./containers/AdomikProgrammatic/pages/SSPMappings'), 'SSPMappings'),
    Seats: lazyNamed(() => import('./containers/AdomikProgrammatic/pages/Seats'), 'Seats'),
  },

  PassendoProgrammatic: {
    Layout: lazy(() => import('src/scenes/Settings/containers/Passendo/Layout')),
    WebsitesMapping: lazy(() => import('src/scenes/Settings/containers/Passendo/WebsitesMapping')),
  },

  Brands: {
    List: lazy(() => import('./containers/Brands/containers/Brands')),
    Websites: lazy(() => import('./containers/Brands/containers/Websites')),
    Layout: lazy(() => import('./containers/Brands/components/Layout')),
  },

  PositionGroups: {
    Index: lazyNamed(() => import('./containers/PositionGroups'), 'PositionGroups'),
    PositionGroupForm: lazyNamed(() => import('./containers/PositionGroups/containers/PositionGroupForm'), 'PositionGroupForm'),
    PositionsForm: lazyNamed(() => import('./containers/PositionGroups/containers/PositionsForm'), 'PositionsForm'),
  },

  PositionGroupsV2,

  PrintProducts: {
    AdMedias: lazy(() => import('./containers/PrintProducts/AdMedias')),
    AdTypes: lazy(() => import('./containers/PrintProducts/AdTypes')),
    Placements: lazy(() => import('./containers/PrintProducts/Placements')),
    Prices: lazy(() => import('./containers/PrintProducts/Prices')),
    Products: lazy(() => import('./containers/PrintProducts/Products')),
    Calendars: lazy(() => import('./containers/PrintProducts/Calendars')),
    SizeConfigs: lazy(() => import('./containers/PrintProducts/SizeConfigs')),
    Availabilities: lazy(() => import('./containers/PrintProducts/Availabilities')),
    Layout: lazy(() => import('./containers/PrintProducts/components/Layout')),
  },

  PrintProducts2: {
    Layout: PrintProductsLayout,
    AdMedias,
    PrintProductsPlacements,
    PrintProductsAdTypes,
    PrintProducts,
    PrintProductsAdPrices,
    Calendars,
    SizeConfigs,
    Availabilities,
  },

  PointOfSaleProducts,

  Websites: {
    WebsitesList: lazy(() => import('./containers/Websites/containers/WebsitesList')),
    WebsiteForm: lazyNamed(() => import('./containers/Websites/containers/WebsiteForm'), 'WebsiteForm'),
  },

  AnalogProducts,
  PartnerFilialManagement,

  SalesUnits: lazyNamed(() => import('./containers/SalesUnits'), 'SalesUnits'),
  ReferencePerAdmedia,
  SpecialDiscounts: lazyNamed(() => import('./containers/SpecialDiscounts'), 'SpecialDiscounts'),
};
